import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { logos, icons, MAIL_FORMATTING } from "../helpers/constants";
import Button from "../components/Buttons/Button";
import LanguagePicker from "../components/LanguagePicker";
import * as request from "../services/FetchService";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../helpers/customHooks";
import TosModal from "../components/Modals/TosModal";
import { useContextData } from "../components/ContextData"; // Adjust the path as necessary
import toast from "react-hot-toast";
import NewButton from "../components/Buttons/NewButton";
import { toggleTheme } from "../assets/css/theme";
import { AnimatePresence, motion } from "framer-motion";

const SignupNew = (props) => {
	const MOBILE = useIsMobile();
	const navigate = useNavigate();
	const { accessToken, setAccessToken } = useContextData();
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [emailConfirm, setEmailConfirm] = useState("");
	const [invalidEmail, setInvalidEmail] = useState(false);
	const [invalidEmailConfirm, setInvalidEmailConfirm] = useState(false);
	const [emailMismatch, setEmailMismatch] = useState(false);
	const [password, setPassword] = useState("");
	const [passwordConfirm, setPasswordConfirm] = useState("");
	const [username, setUsername] = useState("");
    const [devicename, setDevicename] = useState("")
	const [imei, setImei] = useState("");
	const [policy, setPolicy] = useState(false);
	const [location, setLocation] = useState(false);
	const [step, setStep] = useState(1);
	const [errors, setErrors] = useState({});
	const [modals, setModals] = useState({
		tos: false,
		privacy: false,
	});
	const [tema, setTema] = useState(
		() => document.documentElement.getAttribute("data-theme") || "light"
	);
	const [outerShowLang, setOuterShowLang] = useState(false);
	const [onHover, setOnHover] = useState(false);
	const onToggleTheme = () => {
		const curr = toggleTheme()
		setTema(curr)
	}
	const darkmodeIconSrc = tema === "dark" ? icons.sun : icons.moon;
	
	const toggleModal = (modalName) => {
		setModals((prevState) => ({
			...prevState,
			[modalName]: !prevState[modalName],
		}));
	};


        const validateInputs = (page) => {
            let innerErrors = {}
            if(page === 1){
                innerErrors = {
                    email: !MAIL_FORMATTING.test(email),
                    email_confirm: !MAIL_FORMATTING.test(emailConfirm),
                    mismatch: email !== emailConfirm,
                    policy: !policy,
                    location: !location   
                }
            }else if(page === 2){
                innerErrors = {
                    password: password.length<6,
                    password_confirm: passwordConfirm.length<6,
                    password_mismatch: password !== passwordConfirm
                }
            }else if(page === 3){
                innerErrors = {
                    username: username.length<1
                }
            }else if(page === 4){
                innerErrors = {
                    imei: process.env.REACT_APP_FLAVOR === "tracking" && imei.length < 10
                }
            }
            setErrors(innerErrors)
            return innerErrors
        }

	const handleNext = (page) => {
		const hasErrors = validateInputs(page)
		if (!Object.values(hasErrors).some((value) => value === true) && step < 4) {
			setStep(step + 1);
		} else if (!Object.values(hasErrors).some((value) => value === true) && step === 4) {
			createAccount();
		}
	};

	const handleLoginSuccess = async (token) => {
		localStorage.setItem("accesstoken", token);
		await setAccessToken(token);
		navigate("/devices");
	};

	const createAccount = () => {
		const lang = localStorage.getItem("language") ?? "en";
		const data = {
			email: email,
			password: password,
			username: username,
            devicename: devicename,
			language: lang,
			regcode: [imei]
		};
		request.post("", "account/create", data).then((response) => {
			if (response.data.error) {
				toast.error(response.data.message);
				setErrors((prevState) => ({
					...prevState,
					imei: true,
				}));
			} else if (response.data.successful) {
				handleLoginSuccess(response.data.accessToken);
			}
		});
	};

	const goBack = () => {
		if (step === 1) {
			navigate("/login");
		} else {
			setStep(step - 1);
		}
	};

	const handleChange = (type, e) => {
		const value = e.target.value;

		setErrors((prev) => {
			let next = { ...prev };
			switch (type) {
				case "email":
					setEmail(value);
					next.email = !MAIL_FORMATTING.test(value) && value !== "";
					// Compare using the new email value and current confirm value
					next.mismatch = value !== emailConfirm && value !== "";
					break;

				case "emailConfirm":
					setEmailConfirm(value);
					next.email_confirm = !MAIL_FORMATTING.test(value) && value !== "";
					// Compare using the current email value and new confirm value
					next.mismatch = email !== value && value !== "";
					break;

                case "password":
                    setPassword(value);
                    next.password = password < 6 && value !== "";
                    next.password_mismatch = value !== passwordConfirm && value !== "";
                    break;

                case "passwordConfirm":
                    setPasswordConfirm(value);
                    next.password_confirm = passwordConfirm < 6 && value !== "";
                    next.password_mismatch = value !== password && value !== "";
                    break;
                case "username":
                    setUsername(value);
                    next.username = value === ""
                    break;
                case "devicename":
                    setDevicename(value);
                    next.devicename = value === ""
                    break;
                case "imei":
                    setImei(value);
                    next.imei = value === "" && value.length < 10 && value !== "";
                    break;
				default:
					return prev;
			}

			return next;
		});
	};

	const step1 = () => {
		return (
			<>
				<div className="mt-3">
					<h1 className="mb-2">1. {t("signup.email")}</h1>
					<p>{t("signup.email_sub")}</p>
				</div>
				<form>
					<p className="bold mt-3">{t("signup.email")}</p>
					<div className="form-group position-relative mb-5">
						<input
							type="email"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.email && "error-border"}`}
							name="email"
							value={email}
							onChange={(e) => {
								handleChange("email", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault(); // Prevent the default Enter key behavior
									document.getElementById("emailConfirm").focus();
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.email ? t("signup.invalid_email") : errors.mismatch ? t("signup.email_mismatch") : ""}</p>
						</div>
					</div>
					<div className="form-group mt-3">
						<p className="bold">{t("signup.email_confirm")}</p>
						<input
							type="email"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.email_confirm && "error-border"}`}
							name="emailConfirm"
							id="emailConfirm"
							value={emailConfirm}
							onChange={(e) => {
								handleChange("emailConfirm", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();// Prevent the default Enter key behavior
                                    handleNext(1)
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.email_confirm ? t("signup.invalid_email") : errors.mismatch ? t("signup.email_mismatch") : ""}</p>
						</div>
					</div>
				</form>
                <div className="d-flex flex-column position-relative pb-3 mt-5">
                    <div className="d-flex align-items-center justify-content-between">       
                        <p className="grey-blue">
                            <Trans
                                i18nKey="signup.policy_tos"
                                components={{
                                policy: <span className="cursor-pointer underline" onClick={() => toggleModal("tos")} />,
                                terms: <span className="cursor-pointer underline" onClick={() => toggleModal("tos")} />,
                                }}
                                values={{
                                policy: t("signup.policy"),
                                terms: t("signup.terms")
                                }}
                            />
                        </p>
                        <div className={`checkbox-${policy ? "active" : "inactive"} rounded8 size_20 d-flex justify-content-center p-1 cursor-pointer`} onClick={() => !policy ? toggleModal("tos") : ""}>
                            {policy && <img className="white-icon" src={icons.check} alt={""} />}
                        </div>

                    </div>
                    <p className="red bold position-absolute start-0 bottom-0">
                        {!policy && errors.policy ? t("signup.must_accept_policy") : ""}
                    </p>
                </div>
                <div className="d-flex flex-column position-relative pb-3 mt-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <p className="grey-blue">{t("signup.location_data")}</p>
                        <div className={`checkbox-${location ? "active" : "inactive"} rounded8 size_20 d-flex justify-content-center p-1 cursor-pointer`} onClick={() => setLocation(!location)}>
                            {location && <img className="white-icon" src={icons.check} alt={""} />}
                        </div>
                    </div>
                    <p className="red bold position-absolute start-0 bottom-0">
                        {!location && errors.location ? t("signup.must_accept_location") : ""}
                    </p>
                </div>
                <NewButton className="bg-gradient white-white bold mt-5 rounded16 p-3" onClick={() => handleNext(1)}>{t("signup.next")}</NewButton>
			</>
		);
	};

	const step2 = () => {
        return (
			<>
                <div className='mt-3'>
                    <h1 className='mb-2'>2. {t("signup.password")}</h1>
                    <p>{t("signup.password_sub")}</p>
                </div>
				<form>
					<p className="bold mt-3">{t("signup.password")}</p>
					<div className="form-group position-relative mb-5">
						<input
							type="password"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.password && "error-border"}`}
							name="password"
							value={password}
							onChange={(e) => {
								handleChange("password", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault(); // Prevent the default Enter key behavior
									document.getElementById("passwordConfirm").focus();
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.password ? t("signup.invalid_password") : errors.password_mismatch ? t("signup.password_mismatch") : ""}</p>
						</div>
					</div>
					<div className="form-group mt-3">
						<p className="bold">{t("signup.password_confirm")}</p>
						<input
							type="password"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.password_confirm && "error-border"}`}
							name="passwordConfirm"
							id="passwordConfirm"
							value={passwordConfirm}
							onChange={(e) => {
								handleChange("passwordConfirm", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();// Prevent the default Enter key behavior
                                    handleNext(2)
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.password_confirm ? t("signup.invalid_password") : errors.password_mismatch ? t("signup.password_mismatch") : ""}</p>
						</div>
					</div>
				</form>
                <NewButton className="bg-gradient white-white bold mt-5 rounded16 p-3" onClick={() => handleNext(2)}>{t("signup.next")}</NewButton>
			</>
		);
    };
	const step3 = () => {
        return (
			<>
                <div className='mt-3'>
                    <h1 className='mb-2'>3. {t("signup.account_details")}</h1>
                    <p>{t("signup.account_details_sub")}</p>
                </div>
				<form>
					<p className="bold mt-3">{t("signup.username")}</p>
					<div className="form-group position-relative mb-5">
						<input
							type="text"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.username && "error-border"}`}
							name="username"
							value={username}
							onChange={(e) => {
								handleChange("username", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault(); // Prevent the default Enter key behavior
									handleNext(3);
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.username ? t("signup.invalid_username") : ""}</p>
						</div>
					</div>
				</form>
                <NewButton className="bg-gradient white-white bold mt-5 rounded16 p-3" onClick={() => handleNext(3)}>{t("signup.next")}</NewButton>
			</>
		);
    };
	const step4 = () => {
        return (
			<>
                <div className='mt-3'>
                    <h1 className='mb-2'>4. {t("signup.imei")}</h1>
                    <p>{t("signup.imei_sub")}</p>
                </div>
				<form>
					<p className="bold mt-3">{t("login.device_name")}</p>
					<div className="form-group position-relative mb-5">
						<input
							type="text"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.devicename && "error-border"}`}
							name="devicename"
							value={devicename}
							onChange={(e) => {
								handleChange("devicename", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault(); // Prevent the default Enter key behavior
									document.getElementById("imei").focus();
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.devicename ? t("signup.invalid_username") : ""}</p>
						</div>
					</div>
                    <div className="form-group mt-3">
						<p className="bold">{t("signup.imei")}</p>
						<input
							type="text"
							placeholder=""
							className={`form-control rounded16 p-3 ${errors.imei && "error-border"}`}
							name="imei"
							id="imei"
							value={imei}
							onChange={(e) => {
								handleChange("imei", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();// Prevent the default Enter key behavior
                                    handleNext(4);
								}
							}}
						/>
						<div className="d-flex justify-content-between position-relative">
							<p className="red bold position-absolute mt-1">{errors.imei ? t("signup.invalid_imei") : ""}</p>
						</div>
					</div>
				</form>
                <NewButton className="bg-gradient white-white bold mt-5 rounded16 p-3" onClick={() => handleNext(4)}>{t("signup.next")}</NewButton>
			</>
		);
    };

	return (
		<>
			<div className="d-flex flex-column loginfield mx-auto h-100 px-3 px-md-0">
				<div className='d-flex align-self-end gap-3'>
					{process.env.REACT_APP_ASSETS === "swetrack" &&		
						<img onClick={onToggleTheme} src={darkmodeIconSrc} className={`grey-blue-icon position-relative cursor-pointer size_20 align-self-center`} alt="..." />
					}
					<div className="d-flex align-items-center justify-items-center">
						<div className="position-relative">
							<LanguagePicker outerShowLang={outerShowLang} />
						</div>
						<motion.div onClick={() => setOuterShowLang(!outerShowLang)} className="label cursor-pointer" initial={false} animate={{ maxWidth: onHover ? 999 : 0, x: onHover ? 0 : -8 }}>
							<span className="fw-bold">{t("sidebar.language")}</span>
						</motion.div>
					</div>
				</div>
				<NewButton notext={true} icon={icons.chevron_left} iconClass={"grey-blue-icon"} className={`position-absolute my-auto ms-2 border-sm bg-white full-rounded size_36 p-3`} onClick={() => goBack()} />
				<img src={logos.big} className="login_logo mx-auto mb-3" alt="logo" />
				{step === 1 ? step1() : step === 2 ? step2() : step === 3 ? step3() : step === 4 ? step4() : null}
			</div>
			<TosModal show={modals.tos} accept={() => setPolicy(true)} onClose={() => toggleModal("tos")} />
		</>
	);
};

export default SignupNew;
